import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { State } from 'reducers';
import { selectUserAuthenticated } from 'brain-data/store/selectors/authentication.selector';
import { environment } from '../../../../environments/environment';
import { createViewportPublisher } from './viewport/viewport-publisher';
import { deriveViewportState } from './viewport/operator-viewport-state';
import { VIEWPORT_SURFACES } from './viewport/surface-registry';

// BRN-2118 (AC1, AC5, AC6, AC7, AC8): the BRAIN-side "host wrapper" design.md calls for --
// health-probe availability gating, iframe mount wiring, and the postMessage bridge (host side).
// Deliberately native Angular/NgZone-aware code, not a consumer of brain-agents' /embed/loader.js
// (that script cannot itself know about Angular's zones -- see design.md's own "Risks" section:
// "run the postMessage listener outside Angular's zone... unbounded in-zone listeners would
// trigger change detection per message"). Every failure path here is a silent no-op: a fetch
// rejection, a timeout, a malformed message, a missing token -- none of them ever throw, log at
// error level, or surface visibly (AC6's own "never crash/error visibly" discipline, restated for
// this host-side half of the contract).
const HEALTH_PROBE_TIMEOUT_MS = 1800;
const RE_PROBE_INTERVAL_MS = 60_000;
const BRIDGE_PROTOCOL_VERSION = 1;
// Mirrors eStream's IFRAME_LOAD_TIMEOUT_MS (embed-config.ts) -- BRN-2118 L6 remediation, Cluster G.
const IFRAME_LOAD_TIMEOUT_MS = 5_000;
// BRN-2069: BRAIN declares its opaque product identity to the embedded brain-agents chat on the
// embed iframe URL it controls (`?consumerApp=brain`). brain-agents stays product-agnostic -- it
// reads whatever the originating product passes and never guesses; eStream declares `estream`. The
// value MUST match the brain-agents ChatController contract, which accepts only `brain` | `estream`.
const CONSUMER_APP = 'brain';

type BridgeMessage =
  | { v: number; type: 'chat:ready' }
  | { v: number; type: 'chat:token-expired' }
  | { v: number; type: 'chat:close' }
  | { v: number; type: string };

export interface AgentEmbedAttachCallbacks {
  onClose: () => void;
}

export interface AgentEmbedAttachHandle {
  /** Detaches the postMessage listener + token-refresh subscription + any pending load-timeout. */
  detach: () => void;
  /** Bind to the iframe's native `(load)` event -- clears the load-failure timeout/watchdog. */
  notifyLoaded: () => void;
  /** Bind to the iframe's native `(error)` event -- treated identically to a load timeout. */
  notifyError: () => void;
}

@Injectable({ providedIn: 'root' })
export class AgentEmbedService implements OnDestroy {
  private readonly availableSubject = new BehaviorSubject<boolean>(false);
  /** Raw health-probe signal only (pre-auth-gating) -- kept private so `available$` below is the
   * one seam every consumer reads. */
  private readonly probeAvailable$: Observable<boolean> = this.availableSubject.asObservable().pipe(distinctUntilChanged());

  /** Hidden-by-default, positive-signal render (design.md) -- true only when BOTH a genuine 200
   * {status:"ok"} probe response AND an authenticated session are current (BRN-2118 L6 remediation,
   * Cluster H: AC1/AC2 are "Given a signed-in user" -- the launcher must never render pre-login,
   * e.g. on /auth/login, mirroring eStream's authenticated-/portal-only mount). Every other outcome
   * (probe failure, timeout, network error, non-200, malformed body, signed-out) leaves/returns
   * this false. */
  readonly available$: Observable<boolean> = combineLatest([
    this.probeAvailable$,
    this.store.select(selectUserAuthenticated).pipe(map((user) => Boolean(user))),
  ]).pipe(
    map(([probeOk, authenticated]) => probeOk && authenticated),
    distinctUntilChanged(),
  );

  private reprobeHandle: ReturnType<typeof setInterval> | null = null;
  private idleHandle: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly oAuthService: OAuthService,
    private readonly store: Store<State>,
    private readonly router: Router,
  ) {
    this.zone.runOutsideAngular(() => {
      // Idle-scheduled: deferred past the current synchronous boot work so the probe never
      // delays product paint (design.md: "probe is idle-scheduled so it never delays product boot").
      this.idleHandle = setTimeout(() => void this.probeOnce(), 0);
      this.reprobeHandle = setInterval(() => void this.probeOnce(), RE_PROBE_INTERVAL_MS);
    });
  }

  ngOnDestroy(): void {
    if (this.idleHandle !== null) {
      clearTimeout(this.idleHandle);
    }
    if (this.reprobeHandle !== null) {
      clearInterval(this.reprobeHandle);
    }
  }

  get embedUrl(): string {
    return appendConsumerApp(`${environment.agentsEmbedUrl}/embed`, CONSUMER_APP);
  }

  /**
   * Wires the postMessage bridge (host side) for one mounted iframe: relays the current IAM
   * access token on chat:ready and chat:token-expired, and routes chat:close back to the caller
   * (the panel component, so it can close the floating panel). Also arms a load-failure watchdog
   * (BRN-2118 L6 remediation, Cluster G): if the iframe document never fires `load` within
   * IFRAME_LOAD_TIMEOUT_MS, or fires a native `error`, the panel closes and availability flips
   * false immediately -- mirrors eStream's useEmbedBridge failOpen()/IFRAME_LOAD_TIMEOUT_MS, so a
   * health probe that passed but an iframe document that then fails to load (wrong origin, 500,
   * transient network blip) never leaves a blank stuck panel open (AC6). Returns a handle the
   * caller must wire to the iframe's (load)/(error) events and invoke detach() on teardown.
   */
  attachIframe(iframe: HTMLIFrameElement, callbacks: AgentEmbedAttachCallbacks): AgentEmbedAttachHandle {
    const embedOrigin = environment.agentsEmbedUrl;
    let listener: ((event: MessageEvent) => void) | null = null;
    let loaded = false;
    let loadTimeoutHandle: ReturnType<typeof setTimeout> | null = null;

    // BRN-2123 (AC3). One publisher per attached iframe: it holds the revision counter and the
    // last-sent identity, which is what makes "exactly one message per change, at a higher revision"
    // a property of the SEQUENCE rather than of a call site. api-contract.md §1 defines the revision
    // as monotonic PER MOUNT, and a re-attached iframe is a new mount starting again at 1.
    //
    // The state it publishes comes from the ROUTER'S OWN URL and from nothing else — deliberately
    // not from the NgRx store, which is the security review's E2 finding: a store routinely holds
    // more than the page renders (a prefetched list, a fuller entity than the view shows, a sibling
    // route's slice), and the snapshot must contain nothing the operator could not already see. The
    // URL is, by definition, in front of them.
    const viewportPublisher = createViewportPublisher({
      targetOrigin: embedOrigin,
      surfaces: VIEWPORT_SURFACES,
      resolveTarget: () => iframe.contentWindow,
      clock: {
        now: () => new Date(),
        schedule: (callback, delayMs) => setTimeout(callback, delayMs),
        cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
      },
    });
    const publishCurrentUrl = (url: string): void =>
      viewportPublisher.publish(deriveViewportState(url, VIEWPORT_SURFACES));

    const clearLoadTimeout = (): void => {
      if (loadTimeoutHandle !== null) {
        clearTimeout(loadTimeoutHandle);
        loadTimeoutHandle = null;
      }
    };

    const handleLoadFailure = (): void => {
      if (loaded) {
        return;
      }
      clearLoadTimeout();
      this.zone.run(() => {
        this.availableSubject.next(false);
        callbacks.onClose();
      });
    };

    this.zone.runOutsideAngular(() => {
      loadTimeoutHandle = setTimeout(handleLoadFailure, IFRAME_LOAD_TIMEOUT_MS);

      listener = (event: MessageEvent) => {
        try {
          // Exact-match only -- never substring/endsWith/regex containment (security-review.md's
          // cross-cutting ADVISORY constraint, MSRC 2025 postMessage-exploitation research).
          if (event.origin !== embedOrigin) {
            return;
          }
          const message = parseBridgeMessage(event.data);
          if (!message) {
            return;
          }
          if (message.type === 'chat:ready' || message.type === 'chat:token-expired') {
            this.pushToken(iframe, embedOrigin);
            if (message.type === 'chat:ready') {
              // AC3's "after chat:ready" is enforced here and nowhere else: until this call the
              // publisher posts nothing, however many navigations have already happened.
              viewportPublisher.markReady();
            }
          } else if (message.type === 'chat:close') {
            this.zone.run(() => callbacks.onClose());
          }
        } catch {
          // silent no-op -- a malformed/hostile message must never surface visibly.
        }
      };
      window.addEventListener('message', listener);
    });

    const tokenRefreshSubscription: Subscription = this.oAuthService.events
      .pipe(filter((event) => event.type === 'token_refreshed'))
      .subscribe(() => this.pushToken(iframe, embedOrigin));

    // The operator's CURRENT view first (they were already on a page when the chat opened), then
    // every later navigation. `urlAfterRedirects` rather than `url`, so a guard-driven redirect is
    // described by where the operator LANDED and never by where they were sent from.
    publishCurrentUrl(this.router.url);
    const navigationSubscription: Subscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => publishCurrentUrl(event.urlAfterRedirects));

    return {
      detach: () => {
        clearLoadTimeout();
        if (listener) {
          window.removeEventListener('message', listener);
        }
        tokenRefreshSubscription.unsubscribe();
        navigationSubscription.unsubscribe();
        viewportPublisher.dispose();
      },
      notifyLoaded: () => {
        loaded = true;
        clearLoadTimeout();
      },
      notifyError: handleLoadFailure,
    };
  }

  private pushToken(iframe: HTMLIFrameElement, embedOrigin: string): void {
    try {
      const token = this.oAuthService.getAccessToken();
      if (!token || !iframe.contentWindow) {
        return;
      }
      // Exact targetOrigin always, never "*" (api-contract.md contract rule).
      iframe.contentWindow.postMessage({ v: BRIDGE_PROTOCOL_VERSION, type: 'chat:auth', token }, embedOrigin);
    } catch {
      // silent no-op
    }
  }

  private async probeOnce(): Promise<void> {
    try {
      const controller = new AbortController();
      const timeoutHandle = setTimeout(() => controller.abort(), HEALTH_PROBE_TIMEOUT_MS);
      let ok = false;
      try {
        const response = await fetch(`${environment.agentsEmbedUrl}/embed/health`, {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (response.ok) {
          const body: unknown = await response.json();
          ok = Boolean(body && typeof body === 'object' && (body as { status?: unknown }).status === 'ok');
        }
      } finally {
        clearTimeout(timeoutHandle);
      }
      this.zone.run(() => this.availableSubject.next(ok));
    } catch {
      this.zone.run(() => this.availableSubject.next(false));
    }
  }
}

function parseBridgeMessage(data: unknown): BridgeMessage | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const candidate = data as Record<string, unknown>;
  if (candidate['v'] !== BRIDGE_PROTOCOL_VERSION || typeof candidate['type'] !== 'string') {
    return null;
  }
  return { v: BRIDGE_PROTOCOL_VERSION, type: candidate['type'] };
}

/**
 * BRN-2069: appends the declaring product's opaque `consumerApp` identity to the embed document URL
 * so the embedded brain-agents chat learns which product it is running inside directly from the
 * iframe URL it was handed. Built with URL/URLSearchParams so any pre-existing query params are
 * preserved rather than clobbered; falls back to a safe manual append when the configured origin is
 * absent/relative (agentsEmbedUrl unset) so the getter never throws.
 */
export function appendConsumerApp(embedUrl: string, consumerApp: string): string {
  try {
    const url = new URL(embedUrl);
    url.searchParams.set('consumerApp', consumerApp);
    return url.toString();
  } catch {
    const separator = embedUrl.includes('?') ? '&' : '?';
    return `${embedUrl}${separator}consumerApp=${encodeURIComponent(consumerApp)}`;
  }
}
